import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { useTranslation } from "react-i18next";

import LogoTitle from "assets/img/text_logo_white.png";
import type { MaintenanceConfig } from "lib/maintenanceMode";

type MaintenanceProps = {
  config?: MaintenanceConfig;
  initialCheck?: boolean;
  checking?: boolean;
  onRetry?: () => void;
};

const ServerStatus = () => (
  <Box
    aria-hidden="true"
    sx={{
      position: "relative",
      width: { xs: 154, sm: 184 },
      height: { xs: 154, sm: 184 },
      display: "grid",
      placeItems: "center",
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        border: "1px solid rgba(39, 170, 225, 0.28)",
        animation: "maintenancePulse 2.8s ease-out infinite",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 18,
        borderRadius: "50%",
        bgcolor: "rgba(39, 170, 225, 0.1)",
        border: "1px solid rgba(39, 170, 225, 0.18)",
      }}
    />
    <Box
      sx={{
        width: { xs: 92, sm: 108 },
        p: 1.5,
        borderRadius: 3,
        color: "#FFFFFF",
        bgcolor: "#253E66",
        boxShadow: "0 20px 42px rgba(21, 44, 75, 0.26)",
      }}
    >
      {[0, 1, 2].map((row) => (
        <Box
          key={row}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: 24,
            px: 0.8,
            mb: row === 2 ? 0 : 0.75,
            borderRadius: 1.2,
            bgcolor: "rgba(255, 255, 255, 0.08)",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: row === 1 ? "#E8A93B" : "#11CC9A",
              boxShadow: `0 0 0 3px ${
                row === 1 ? "rgba(232,169,59,.14)" : "rgba(17,204,154,.12)"
              }`,
            }}
          />
          <Box
            sx={{
              height: 3,
              flex: 1,
              borderRadius: 2,
              bgcolor: "rgba(255, 255, 255, 0.26)",
            }}
          />
        </Box>
      ))}
    </Box>
    <Box
      sx={{
        position: "absolute",
        right: { xs: 12, sm: 14 },
        bottom: { xs: 19, sm: 23 },
        width: 42,
        height: 42,
        display: "grid",
        placeItems: "center",
        borderRadius: "50%",
        color: "#FFFFFF",
        bgcolor: "#27AAE1",
        border: "4px solid #FFFFFF",
        boxShadow: "0 8px 18px rgba(39, 170, 225, 0.34)",
      }}
    >
      <SyncRoundedIcon
        sx={{
          fontSize: 22,
          animation: "maintenanceSpin 2.2s linear infinite",
        }}
      />
    </Box>
  </Box>
);

const Maintenance = ({
  config,
  initialCheck = false,
  checking = false,
  onRetry,
}: MaintenanceProps) => {
  const { t } = useTranslation("global");

  return (
    <Box
      component="main"
      sx={{
        "@keyframes maintenanceSpin": {
          to: { transform: "rotate(360deg)" },
        },
        "@keyframes maintenancePulse": {
          "0%": { transform: "scale(.78)", opacity: 0 },
          "30%": { opacity: 1 },
          "100%": { transform: "scale(1.08)", opacity: 0 },
        },
        minHeight: "100dvh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        color: "#253E66",
        background:
          "radial-gradient(circle at 13% 18%, rgba(39,170,225,.34), transparent 26%), radial-gradient(circle at 88% 84%, rgba(17,204,154,.16), transparent 28%), linear-gradient(137deg, #172B4A 0%, #253E66 52%, #205F83 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.18,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.08) 1px, transparent 1px)",
          backgroundSize: "54px 54px",
          maskImage:
            "linear-gradient(to bottom right, rgba(0,0,0,.9), transparent 72%)",
        }}
      />

      <Box
        component="header"
        sx={{
          position: "relative",
          zIndex: 1,
          width: "min(1120px, calc(100% - 40px))",
          mx: "auto",
          pt: { xs: 3, sm: 4.5 },
        }}
      >
        <Box
          component="img"
          src={LogoTitle}
          alt="tradEAsy"
          sx={{ width: { xs: 150, sm: 180 }, height: "auto" }}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          width: "min(1120px, calc(100% - 28px))",
          mx: "auto",
          py: { xs: 3, sm: 5 },
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 960,
            minHeight: { sm: 500 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 280px" },
            alignItems: "center",
            gap: { xs: 2.5, md: 6 },
            px: { xs: 3, sm: 6, md: 8 },
            py: { xs: 4, sm: 6 },
            borderRadius: { xs: 4, sm: 6 },
            bgcolor: "rgba(255, 255, 255, 0.97)",
            boxShadow: "0 30px 90px rgba(7, 24, 47, 0.34)",
          }}
        >
          <Box
            sx={{
              order: { xs: 2, md: 1 },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mb: 2.5,
                px: 1.5,
                py: 0.8,
                borderRadius: 10,
                bgcolor: "rgba(232, 169, 59, 0.13)",
                color: "#A46B00",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.08em",
              }}
            >
              {initialCheck ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DnsRoundedIcon sx={{ fontSize: 17 }} />
              )}
              {initialCheck
                ? t("maintenance.checking")
                : t("maintenance.status")}
            </Box>

            <Typography
              component="h1"
              sx={{
                maxWidth: 610,
                color: "#253E66",
                fontFamily: "Catamaran, sans-serif",
                fontSize: { xs: 34, sm: 48, md: 54 },
                fontWeight: 700,
                lineHeight: 0.98,
                letterSpacing: "-0.035em",
              }}
            >
              {initialCheck
                ? t("maintenance.checkingTitle")
                : config?.title || t("maintenance.title")}
            </Typography>

            <Typography
              sx={{
                maxWidth: 580,
                mt: 2.5,
                color: "#68778E",
                fontSize: { xs: 16, sm: 18 },
                lineHeight: 1.5,
              }}
            >
              {initialCheck
                ? t("maintenance.checkingMessage")
                : config?.message || t("maintenance.message")}
            </Typography>

            {!initialCheck && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "center", md: "flex-start" },
                    gap: 1,
                    mt: 3,
                    color: "#253E66",
                  }}
                >
                  <AccessTimeRoundedIcon
                    sx={{ fontSize: 20, color: "#27AAE1" }}
                  />
                  <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    {t("maintenance.retryLater")}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  onClick={onRetry}
                  disabled={checking}
                  startIcon={
                    checking ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshRoundedIcon />
                    )
                  }
                  sx={{
                    mt: 3.5,
                    px: 2.8,
                    py: 1.15,
                    borderRadius: 2.2,
                    fontWeight: 800,
                    boxShadow: "0 10px 24px rgba(39, 170, 225, 0.24)",
                  }}
                >
                  {checking
                    ? t("maintenance.checkingAction")
                    : t("maintenance.retryAction")}
                </Button>
              </>
            )}
          </Box>

          <Box
            sx={{
              order: { xs: 1, md: 2 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ServerStatus />
          </Box>
        </Box>
      </Box>

      <Typography
        component="footer"
        sx={{
          position: "relative",
          zIndex: 1,
          pb: 3,
          color: "rgba(255,255,255,.66)",
          fontSize: 12,
          textAlign: "center",
          letterSpacing: "0.04em",
        }}
      >
        tradEAsy · {t("maintenance.footer")}
      </Typography>
    </Box>
  );
};

export default Maintenance;
